/**
 * Firestore access for calibre lineages (revision lines) — Phase 2 admin UI of
 * the design memo (Confluence 2288156674). Rules are in calibreLineageLogic.ts.
 *
 * Storage:
 *   - `calibre_lineages/{id}` — `{ baseLabel, manufacturerId, description? }`,
 *     doc id == slug (shared-types `CalibreLineage`).
 *   - membership and revision facts live on each calibre
 *     (`custom_calibres/{id}.calibreLineageId`, revisionLabel, active years,
 *     isCurrent, isLineagePlaceholder). Members are found by query — there is
 *     no member list on the lineage, the same shape as calibre families.
 *
 * Every write here is IMMEDIATE (not part of the calibre form's Save), like
 * family membership. A cleared optional is deleted with deleteField — never
 * written as null or left behind by an undefined that stripUndefined drops.
 *
 * Firestore rules: `calibre_lineages` needs its own admin-write rule in
 * watchlock (the default-deny catch-all covers it otherwise).
 */
import {
  arrayUnion,
  collection,
  deleteField,
  doc,
  getDoc,
  getDocs,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
  writeBatch,
} from 'firebase/firestore';
import {
  db,
  deriveManufacturerSlug,
  findReferencesForCalibre,
  getReferencesByDocIds,
  saveCustomCalibre,
  type CustomCalibreEntry,
} from './firebase';
import { groupRefsByBrandAka, type BrandAkaGroup } from './calibreLineageLogic';
import type { WatchReference } from '../types';
import type { CalibreLineage } from '@watchkeeper/shared-types';

const LINEAGES = 'calibre_lineages';
const CALIBRES = 'custom_calibres';
const MANUFACTURERS = 'movement_manufacturers';

/** A lineage as stored (shared-types `CalibreLineage`, v1.141.0). */
export type CalibreLineageEntry = CalibreLineage;

export async function getLineagesForManufacturer(manufacturerId: string): Promise<CalibreLineageEntry[]> {
  const snap = await getDocs(query(collection(db, LINEAGES), where('manufacturerId', '==', manufacturerId)));
  return snap.docs
    .map((d) => ({ ...(d.data() as Omit<CalibreLineageEntry, 'id'>), id: d.id }))
    .sort((a, b) => a.baseLabel.localeCompare(b.baseLabel));
}

export async function getLineageById(id: string): Promise<CalibreLineageEntry | null> {
  const snap = await getDoc(doc(db, LINEAGES, id));
  return snap.exists() ? { ...(snap.data() as Omit<CalibreLineageEntry, 'id'>), id: snap.id } : null;
}

export async function getLineageMembers(lineageId: string): Promise<CustomCalibreEntry[]> {
  const snap = await getDocs(query(collection(db, CALIBRES), where('calibreLineageId', '==', lineageId)));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }) as CustomCalibreEntry);
}

/**
 * Every calibre a manufacturer's revision lines may hold: the ones it makes
 * (`manufacturerId`) and the ones it ORIGINALLY made that someone else makes
 * now (`originalManufacturerId`, shared-types 1.143.0 — ETA 6498-1 joins the
 * Unitas 6498 line). Two equality queries merged, since Firestore can't OR
 * across fields. The "Find revision lines" and member-picker input.
 */
export async function getCalibresForManufacturer(manufacturerId: string): Promise<CustomCalibreEntry[]> {
  const [primary, original] = await Promise.all([
    getDocs(query(collection(db, CALIBRES), where('manufacturerId', '==', manufacturerId))),
    getDocs(query(collection(db, CALIBRES), where('originalManufacturerId', '==', manufacturerId))),
  ]);
  const byId = new Map<string, CustomCalibreEntry>();
  for (const d of [...primary.docs, ...original.docs]) byId.set(d.id, { id: d.id, ...d.data() } as CustomCalibreEntry);
  return [...byId.values()];
}

/** A calibre may join this manufacturer's revision lines (made, or originally made, by it). */
export function calibreBelongsToMaker(
  c: Pick<CustomCalibreEntry, 'manufacturerId' | 'originalManufacturerId'>,
  manufacturerId: string,
): boolean {
  return c.manufacturerId === manufacturerId || c.originalManufacturerId === manufacturerId;
}

/**
 * Stems the curator marked "Not a revision" in Find revision lines (memo 5.8),
 * stored on the manufacturer as `lineageDismissedStems` (normalised keys).
 * Admin-only; not in shared-types because nothing else reads it.
 */
export async function getDismissedRevisionStems(manufacturerId: string): Promise<string[]> {
  const snap = await getDoc(doc(db, MANUFACTURERS, manufacturerId));
  const v = snap.data()?.lineageDismissedStems;
  return Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : [];
}

export async function dismissRevisionStem(manufacturerId: string, stemKey: string): Promise<void> {
  await updateDoc(doc(db, MANUFACTURERS, manufacturerId), {
    lineageDismissedStems: arrayUnion(stemKey),
    updatedAt: serverTimestamp(),
  });
}

/** Every member of every lineage of one manufacturer, by lineage id. */
export async function getLineageMembersForManufacturer(
  manufacturerId: string,
): Promise<Map<string, CustomCalibreEntry[]>> {
  const out = new Map<string, CustomCalibreEntry[]>();
  for (const c of await getCalibresForManufacturer(manufacturerId)) {
    if (!c.calibreLineageId) continue;
    out.set(c.calibreLineageId, [...(out.get(c.calibreLineageId) ?? []), c]);
  }
  return out;
}

/**
 * Create or update a lineage. A new one takes the slug of its label as its id
 * and refuses to overwrite an existing doc.
 */
export async function saveLineage(
  draft: { baseLabel: string; manufacturerId: string; description?: string },
  existingId?: string,
): Promise<string> {
  const baseLabel = draft.baseLabel.trim();
  if (!baseLabel) throw new Error('A revision line needs a label');
  if (!draft.manufacturerId) throw new Error('A revision line needs a manufacturer');
  const description = draft.description?.trim();
  if (existingId) {
    await setDoc(
      doc(db, LINEAGES, existingId),
      {
        baseLabel,
        manufacturerId: draft.manufacturerId,
        description: description ? description : deleteField(),
        updatedAt: serverTimestamp(),
      },
      { merge: true },
    );
    return existingId;
  }
  const id = deriveManufacturerSlug(baseLabel);
  if (!id) throw new Error('That label makes an empty id');
  if ((await getDoc(doc(db, LINEAGES, id))).exists()) {
    throw new Error(`A revision line "${id}" already exists`);
  }
  await setDoc(doc(db, LINEAGES, id), {
    baseLabel,
    manufacturerId: draft.manufacturerId,
    ...(description ? { description } : {}),
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  return id;
}

/** The lineage fields, all cleared — what leaving a lineage writes. */
function clearedLineageFields() {
  return {
    calibreLineageId: deleteField(),
    revisionLabel: deleteField(),
    activeFromYear: deleteField(),
    activeUntilYear: deleteField(),
    isCurrent: deleteField(),
    isLineagePlaceholder: deleteField(),
    activeDatesSource: deleteField(),
    activeDatesConfidence: deleteField(),
  };
}

/** Delete a lineage and take every member out of it, atomically. */
export async function deleteLineage(lineageId: string, memberIds: readonly string[]): Promise<void> {
  const batch = writeBatch(db);
  for (const id of memberIds) {
    batch.update(doc(db, CALIBRES, id), { ...clearedLineageFields(), updatedAt: serverTimestamp() });
  }
  batch.delete(doc(db, LINEAGES, lineageId));
  await batch.commit();
}

export interface RevisionFields {
  /** Null = leave the lineage (clears every lineage field). */
  calibreLineageId: string | null;
  revisionLabel?: string;
  activeFromYear?: number;
  activeUntilYear?: number;
  isCurrent?: boolean;
  isLineagePlaceholder?: boolean;
  /**
   * 'ai' when the years were applied from a lineage suggestion (Phase 5), so a
   * later suggestion may revise them. Absent = the curator owns the years —
   * the default for every manual save. Dropped when there are no years.
   */
  activeDatesSource?: 'ai';
  activeDatesConfidence?: 'high' | 'medium' | 'low';
}

/**
 * Write one calibre's revision fields. Undefined / blank optionals are DELETED.
 *
 * Making this calibre current un-marks every other current member of the same
 * lineage in the same batch, so "one current per lineage" holds even if two
 * curators race. A placeholder is never current: the flag is dropped.
 */
export async function setCalibreRevision(calibreId: string, fields: RevisionFields): Promise<void> {
  const ref = doc(db, CALIBRES, calibreId);
  if (!fields.calibreLineageId) {
    const batch = writeBatch(db);
    batch.update(ref, { ...clearedLineageFields(), updatedAt: serverTimestamp() });
    await batch.commit();
    return;
  }
  const isCurrent = fields.isCurrent === true && fields.isLineagePlaceholder !== true;
  const batch = writeBatch(db);
  if (isCurrent) {
    for (const m of await getLineageMembers(fields.calibreLineageId)) {
      if (m.id !== calibreId && m.isCurrent) {
        batch.update(doc(db, CALIBRES, m.id), { isCurrent: deleteField(), updatedAt: serverTimestamp() });
      }
    }
  }
  const label = fields.revisionLabel?.trim();
  batch.update(ref, {
    calibreLineageId: fields.calibreLineageId,
    revisionLabel: label ? label : deleteField(),
    activeFromYear: typeof fields.activeFromYear === 'number' ? fields.activeFromYear : deleteField(),
    activeUntilYear: typeof fields.activeUntilYear === 'number' ? fields.activeUntilYear : deleteField(),
    isCurrent: isCurrent ? true : deleteField(),
    isLineagePlaceholder: fields.isLineagePlaceholder === true ? true : deleteField(),
    ...datesProvenance(fields),
    updatedAt: serverTimestamp(),
  });
  await batch.commit();
}

/** The AI stamp on a revision's years: written only with 'ai' and at least one year, else deleted. */
function datesProvenance(fields: RevisionFields) {
  const hasYears = typeof fields.activeFromYear === 'number' || typeof fields.activeUntilYear === 'number';
  if (fields.activeDatesSource === 'ai' && hasYears) {
    return {
      activeDatesSource: 'ai' as const,
      activeDatesConfidence: fields.activeDatesConfidence ?? deleteField(),
    };
  }
  return { activeDatesSource: deleteField(), activeDatesConfidence: deleteField() };
}

/** The lineage's current member (for the superseded-revision warning), or null. */
export async function getCurrentRevision(lineageId: string): Promise<CustomCalibreEntry | null> {
  const snap = await getDocs(
    query(collection(db, CALIBRES), where('calibreLineageId', '==', lineageId), where('isCurrent', '==', true)),
  );
  const d = snap.docs[0];
  return d ? ({ id: d.id, ...d.data() } as CustomCalibreEntry) : null;
}

/** Every lineage placeholder in the catalogue — the Needs Assessment queue's input. */
export async function getLineagePlaceholders(): Promise<CustomCalibreEntry[]> {
  const snap = await getDocs(query(collection(db, CALIBRES), where('isLineagePlaceholder', '==', true)));
  return snap.docs
    .map((d) => ({ id: d.id, ...d.data() }) as CustomCalibreEntry)
    .sort((a, b) => a.name.localeCompare(b.name));
}

/**
 * Split folded aliases out of `source` into calibres of their own, joined with
 * `source` in one lineage (memo 5.7, the Seiko VK63A–D case).
 *
 * For each chosen alias: a NEW calibre named `name`, with the source's
 * manufacturer and brand and a COPY of its data (the curator corrects the specs
 * per revision afterwards), the alias moved onto it, and its revision label.
 * The source keeps every alias that was not split, joins the lineage, and is
 * marked as the placeholder when `markSourcePlaceholder` (the usual case: the
 * bare name stands for "revision unspecified").
 *
 * Creates go through saveCustomCalibre with forceCreate so the name-conflict
 * machinery stays the single writer of new calibres. Refs are not touched —
 * moving them is the reassignment tool's job, one brand AKA at a time.
 */
export async function splitFoldedAliases(input: {
  source: CustomCalibreEntry;
  lineageId: string;
  splits: Array<{ alias: string; name: string; revisionLabel: string }>;
  markSourcePlaceholder: boolean;
}): Promise<string[]> {
  const { source, lineageId, splits, markSourcePlaceholder } = input;
  const splitAliases = new Set(splits.map((s) => s.alias));
  const createdIds: string[] = [];
  // Tiers are grades of ONE calibre; a new revision starts without them.
  const { tiers: _omitTiers, ...dataWithoutTiers } = source.data ?? {};
  void _omitTiers;
  for (const s of splits) {
    const aliasDetail = (source.aliasDetails ?? []).filter((a) => a.name === s.alias);
    const id = await saveCustomCalibre(
      s.name,
      dataWithoutTiers,
      source.brandName,
      [s.alias],
      undefined,
      undefined,
      false,
      undefined,
      undefined,
      true, // forceCreate: a revision is a new calibre by definition
      source.manufacturerId ?? undefined,
      aliasDetail.length > 0 ? aliasDetail : [{ name: s.alias }],
      source.brandId ?? null,
      source.source,
    );
    await setCalibreRevision(id, { calibreLineageId: lineageId, revisionLabel: s.revisionLabel });
    createdIds.push(id);
  }
  // The split aliases now belong to the new records; take them off the source.
  const batch = writeBatch(db);
  batch.update(doc(db, CALIBRES, source.id), {
    alsoKnownAs: (source.alsoKnownAs ?? []).filter((a) => !splitAliases.has(a)),
    aliasDetails: (source.aliasDetails ?? []).filter((a) => !splitAliases.has(a.name)),
    calibreLineageId: lineageId,
    ...(markSourcePlaceholder ? { isLineagePlaceholder: true, isCurrent: deleteField() } : {}),
    updatedAt: serverTimestamp(),
  });
  await batch.commit();
  return createdIds;
}

/**
 * Move ONE brand-scoped alias from `fromId` to `toId` (memo 9.1: the AKA moves
 * with its refs by default, so the next import for that brand lands on the
 * revision, not back on the placeholder).
 *
 * Only the `aliasDetails` row for that brand moves. The flat `alsoKnownAs`
 * string leaves the source only when no other row there still uses the name —
 * one alias name can carry several brand-scoped rows.
 */
export async function moveBrandAlias(input: {
  fromId: string;
  toId: string;
  akaName: string;
  brandId: string;
}): Promise<void> {
  const [fromSnap, toSnap] = await Promise.all([
    getDoc(doc(db, CALIBRES, input.fromId)),
    getDoc(doc(db, CALIBRES, input.toId)),
  ]);
  if (!fromSnap.exists() || !toSnap.exists()) throw new Error('Calibre not found');
  const from = fromSnap.data() as CustomCalibreEntry;
  const to = toSnap.data() as CustomCalibreEntry;
  const isRow = (a: { name: string; brandId?: string }) => a.name === input.akaName && a.brandId === input.brandId;
  const moved = (from.aliasDetails ?? []).find(isRow) ?? { name: input.akaName, brandId: input.brandId };
  const fromDetails = (from.aliasDetails ?? []).filter((a) => !isRow(a));
  const fromAka = fromDetails.some((a) => a.name === input.akaName)
    ? from.alsoKnownAs ?? []
    : (from.alsoKnownAs ?? []).filter((a) => a !== input.akaName);
  const toDetails = (to.aliasDetails ?? []).some(isRow) ? to.aliasDetails ?? [] : [...(to.aliasDetails ?? []), moved];
  const toAka = (to.alsoKnownAs ?? []).includes(input.akaName)
    ? to.alsoKnownAs ?? []
    : [...(to.alsoKnownAs ?? []), input.akaName];
  const batch = writeBatch(db);
  batch.update(doc(db, CALIBRES, input.fromId), {
    aliasDetails: fromDetails,
    alsoKnownAs: fromAka,
    updatedAt: serverTimestamp(),
  });
  batch.update(doc(db, CALIBRES, input.toId), {
    aliasDetails: toDetails,
    alsoKnownAs: toAka,
    updatedAt: serverTimestamp(),
  });
  await batch.commit();
}

/**
 * A lineage member's linked refs, grouped by its BRAND-SCOPED aliases (the
 * reassignment tool and the Needs Assessment queue read the same thing).
 *
 * `findReferencesForCalibre` reports the CACHED canonical name ahead of a ref's
 * own text, which hides the alias the ref came in under, so the ref documents
 * are read (one read per linked ref, on demand) and grouped on their own
 * calibre string — the evidence.
 */
export async function loadBrandAkaGroups(calibre: Pick<CustomCalibreEntry, 'id' | 'aliasDetails'>): Promise<{
  groups: BrandAkaGroup[];
  docs: Map<string, WatchReference>;
  fallbackCount: number;
}> {
  const res = await findReferencesForCalibre({ calibreId: calibre.id, mode: 'linked' });
  const byBrand = new Map<string, string[]>();
  for (const c of res.candidates) byBrand.set(c.brandId, [...(byBrand.get(c.brandId) ?? []), c.refId]);
  const docs = new Map<string, WatchReference>();
  await Promise.all(
    [...byBrand].map(async ([brandId, ids]) => {
      for (const [id, ref] of await getReferencesByDocIds(brandId, ids)) docs.set(`${brandId}/${id}`, ref);
    }),
  );
  const refs = res.candidates.map((c) => ({
    brandId: c.brandId,
    refId: c.refId,
    isVariant: c.isVariant,
    calibre: docs.get(`${c.brandId}/${c.refId}`)?.calibre?.trim() || c.calibre,
  }));
  const groups = groupRefsByBrandAka(calibre.aliasDetails ?? [], refs);
  const grouped = new Set(groups.flatMap((g) => g.refs.map((r) => `${r.brandId}/${r.refId}`)));
  return { groups, docs, fallbackCount: refs.filter((r) => !grouped.has(`${r.brandId}/${r.refId}`)).length };
}
